import { CloseOutlined, RefreshOutlined, SaveOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Dialog, FormControlLabel, IconButton, Paper, Switch, SwitchProps, Tooltip, Typography, styled } from "@mui/material";
import { isAxiosError } from "axios";
import { ContentProps, Toast } from "primereact/toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { getApiErrorMessage, setToastRefToOpen } from "../../../functions/functions";
import { CustomerFeatureType, CustomerType } from "../../../types/types";
import { findCustomerFeatures, updateCustomerFeatures } from "../../../resources/Api/customerParameter/customerFeatures";

type FeatureKey = Exclude<keyof CustomerFeatureType, "customerId">;
const featureGroups: { title: string; items: { key: FeatureKey; label: string; description: string }[] }[] = [
    {
        title: "Funcionalidades atuais",
        items: [
            { key: "useVideoTelemetry", label: "Videotelemetria", description: "Acesso ao módulo de vídeo." },
            { key: "useCheckList", label: "Checklist", description: "Acesso aos checklists de veículos." },
            { key: "useTurnstile", label: "ônibus escolar", description: "Acesso à funcionalidade de controle de acesso e gestão de transporte escolar." },
        ],
    },
    {
        title: "Operação e relatórios",
        items: [
            { key: "useWorkSchedules", label: "Escalas de trabalho", description: "Cadastro das escalas dos condutores e acesso ao relatório de jornada de trabalho." },
            { key: "useCalculatedRoutes", label: "Rotas calculadas", description: "Rotas previstas, acompanhamento e alertas de desvio. A rota simples permanece disponível." },
            { key: "useDriverLogin", label: "Permitir login de condutores", description: "Permite entrar com um acesso de condutor ativo. Desativar também bloqueia as próximas requisições das sessões já abertas, sem remover acessos ou vínculos." },
            { key: "useDriverControl", label: "Controle de condutores", description: "Limita cada acesso de condutor aos veículos liberados pelo gestor. Desativado, mantém a listagem geral do cliente." },
        ],
    },
    {
        title: "Consulta pública",
        items: [
            { key: "useTransparencyPortalLink", label: "Link do portal da transparência", description: "O gestor do cliente gera um link público, sem login, que mostra apenas a placa, o tipo e a localização dos veículos no mapa. Desativar derruba o link em até 30 segundos." },
        ],
    },
    {
        title: "Experiência",
        items: [
            { key: "useIntroTour", label: "Tour de introdução", description: "Mostra, uma vez por usuário, um tour pelos botões do mapa, pela grade de veículos e pelo menu lateral ao abrir o rastreamento. Condutores não veem o tour." },
        ],
    },
];

type Props = { customer: CustomerType | null; onClose: () => void };

const GreenSwitch = styled((props: SwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    flexShrink: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 2,
        transitionDuration: "300ms",
        "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": { backgroundColor: "#65C466", opacity: 1, border: 0 },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": { color: "#33cf4d", border: "6px solid #fff" },
        "&.Mui-disabled .MuiSwitch-thumb": { color: theme.palette.grey[100] },
        "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
    },
    "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 22, height: 22 },
    "& .MuiSwitch-track": {
        borderRadius: 13,
        backgroundColor: "#E9E9EA",
        opacity: 1,
        transition: theme.transitions.create(["background-color"], { duration: 500 }),
    },
}));

const CustomerFeaturesModal = ({ customer, onClose }: Props) => {
    const [cookies] = useCookies(["token"]);
    const token: string = cookies.token || sessionStorage.getItem("token") || "";
    const customerId = customer?.id;
    const toastRef = useRef<Toast>(null);
    const requestVersion = useRef(0);
    const savingRef = useRef(false);
    const [features, setFeatures] = useState<CustomerFeatureType | null>(null);
    const [original, setOriginal] = useState<CustomerFeatureType | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [confirmingClose, setConfirmingClose] = useState(false);

    const showMessage = useCallback((message: string, status: "warn" | "success" | "error") => {
        toastRef.current?.clear();
        setToastRefToOpen(toastRef, message, status === "success" ? "Sucesso!" : "Atenção!", status, undefined, 3000, false);
    }, []);

    const loadFeatures = useCallback(async () => {
        const request = ++requestVersion.current;
        setFeatures(null);
        setOriginal(null);
        setLoadFailed(false);
        setConfirmingClose(false);
        savingRef.current = false;
        setSaving(false);
        toastRef.current?.clear();
        if (!customerId || customerId <= 0) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const result = await findCustomerFeatures(token, customerId);
            if (request !== requestVersion.current) return;
            setFeatures(result);
            setOriginal(result);
        } catch (error) {
            if (request !== requestVersion.current) return;
            setLoadFailed(true);
            showMessage(getApiErrorMessage(error, "Não foi possível carregar os parâmetros deste cliente."), "error");
        } finally {
            if (request === requestVersion.current) setLoading(false);
        }
    }, [customerId, token, showMessage]);

    useEffect(() => {
        const requests = requestVersion;
        void loadFeatures();
        return () => { requests.current++; };
    }, [loadFeatures]);

    const changed = features !== null && original !== null && featureGroups.some((group) =>
        group.items.some(({ key }) => features[key] !== original[key])
    );

    const save = async () => {
        if (!features || features.customerId !== customerId || loading || savingRef.current || confirmingClose) return;
        if (!changed) {
            showMessage("Nada foi alterado.", "warn");
            return;
        }
        const request = ++requestVersion.current;
        savingRef.current = true;
        setSaving(true);
        toastRef.current?.clear();
        try {
            const updated = await updateCustomerFeatures(token, features);
            if (request !== requestVersion.current) return;
            setFeatures(updated);
            setOriginal(updated);
            showMessage("Parâmetros do cliente salvos com sucesso.", "success");
        } catch (error) {
            if (request !== requestVersion.current) return;
            showMessage(
                getApiErrorMessage(error, "Não foi possível salvar os parâmetros do cliente. Tente novamente."),
                isAxiosError(error) && error.response?.data?.reason ? "warn" : "error",
            );
        } finally {
            if (request === requestVersion.current) {
                savingRef.current = false;
                setSaving(false);
            }
        }
    };

    const close = () => {
        if (savingRef.current || confirmingClose) return;
        if (!changed) {
            onClose();
            return;
        }
        toastRef.current?.clear();
        setConfirmingClose(true);
        setToastRefToOpen(toastRef, "Existem dados não salvos, deseja realmente cancelar?", "Atenção!", "warn", {
            content: (props: ContentProps) => (
                <Box className="tw-flex tw-flex-1 tw-flex-col tw-font-prometo">
                    <Typography>{props.message.summary}</Typography>
                    <Box className="tw-my-3">{props.message.detail}</Box>
                    <Box className="tw-flex tw-justify-end tw-gap-2">
                        <Button sx={{ color: "inherit", textTransform: "none" }} onClick={() => {
                            toastRef.current?.clear();
                            setConfirmingClose(false);
                            onClose();
                        }}>Sim</Button>
                        <Button sx={{ color: "inherit", textTransform: "none" }} onClick={() => {
                            toastRef.current?.clear();
                            setConfirmingClose(false);
                        }}>Não</Button>
                    </Box>
                </Box>
            ),
        }, undefined, true);
    };

    return (
        <Dialog
            open={Boolean(customer)}
            onClose={close}
            fullWidth
            maxWidth="md"
            aria-labelledby="customer-parameter-dialog-title"
            PaperProps={{
                className: "parameters-container tw-overflow-hidden tw-rounded-2xl tw-bg-gtr-surface tw-font-prometo tw-text-gtr-text tw-shadow-gtr-modal",
                sx: {
                    backgroundColor: "rgb(var(--gtr-surface-panel))",
                    backgroundImage: "none",
                    boxShadow: "var(--gtr-shadow-modal)",
                    borderRadius: 4,
                    padding: { xs: 1, sm: 2.5 },
                    width: "100%",
                    maxHeight: { xs: "calc(100dvh - 24px)", sm: "calc(100dvh - 64px)" },
                    overflow: "hidden",
                },
            }}
        >
            <Toast ref={toastRef} position="center" baseZIndex={1500} />
            <Box className="tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden">
                <Paper elevation={0} className="tw-mb-4 tw-flex-none tw-bg-gtr-sidebar tw-text-white" sx={{ backgroundImage: "linear-gradient(to right, #006C77, #09414A)", borderRadius: 3, padding: "8px 12px" }}>
                    <Box className="tw-flex tw-min-h-[45px] tw-items-center tw-gap-3">
                        <Tooltip title={customer?.reducedName || customer?.name || "Parâmetros do cliente"} arrow describeChild>
                            <Typography id="customer-parameter-dialog-title" variant="body1" className="!tw-min-w-0 !tw-flex-1 !tw-truncate !tw-font-prometo !tw-font-bold !tw-text-white" sx={{ fontSize: { xs: 15, sm: 18 } }}>
                                Parâmetros — {customer?.reducedName || customer?.name}
                            </Typography>
                        </Tooltip>
                        <Tooltip title="Fechar" arrow>
                            <span><IconButton onClick={close} disabled={saving || confirmingClose} aria-label="Fechar parâmetros do cliente" className="!tw-h-[42px] !tw-w-[42px] !tw-text-white hover:!tw-bg-white/10"><CloseOutlined /></IconButton></span>
                        </Tooltip>
                    </Box>
                </Paper>
                <Box className="parameters-content tw-min-h-0 tw-overflow-y-auto tw-overscroll-contain tw-rounded-xl tw-p-2 sm:tw-p-3" aria-busy={loading || saving} sx={{ scrollbarWidth: "thin", scrollbarColor: "#94a3b8 transparent" }}>
                    {loading && <Box className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-py-8" role="status" aria-live="polite">
                        <CircularProgress size={24} sx={{ color: "#00B700" }} />
                        <Typography className="!tw-font-prometo !tw-text-gtr-text">Carregando parâmetros do cliente...</Typography>
                    </Box>}
                    {loadFailed && <Box className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-6">
                        <Typography className="!tw-font-prometo !tw-text-gtr-text-muted">Os parâmetros não foram carregados.</Typography>
                        <Button type="button" className="login-button" startIcon={<RefreshOutlined />} onClick={loadFeatures}>Tentar novamente</Button>
                    </Box>}
                    {features && featureGroups.map((group) => (
                        <Box component="section" key={group.title} aria-label={group.title} className="tw-mb-5 last:tw-mb-0">
                            <Typography variant="subtitle1" className="!tw-mb-3 !tw-font-prometo !tw-font-bold !tw-text-gtr-text">{group.title}</Typography>
                            <Box className="tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-4 sm:tw-grid-cols-2">
                                {group.items.map(({ key, label, description }) => (
                                    <Tooltip key={key} title={key === "useTurnstile" ? "Em desenvolvimento" : ""} arrow>
                                        <Box className="tw-min-w-0">
                                            <FormControlLabel
                                                sx={{ margin: 0, gap: 1, alignItems: "flex-start" }}
                                                control={<GreenSwitch checked={features[key] ?? false} disabled={saving || confirmingClose || key === "useTurnstile"} inputProps={{ "aria-describedby": `customer-feature-${key}-description` }} onChange={(event) => {
                                                    const checked = event.target.checked;
                                                    setFeatures((current) => current ? { ...current, [key]: checked } : current);
                                                }} />}
                                                label={<Typography className="!tw-font-prometo !tw-text-gtr-text" sx={{ lineHeight: "26px" }}>{label}</Typography>}
                                            />
                                            <Typography id={`customer-feature-${key}-description`} variant="caption" component="div" className="!tw-mt-1 !tw-pl-[50px] !tw-font-prometo !tw-text-gtr-text-muted">{description}</Typography>
                                        </Box>
                                    </Tooltip>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
                <Box className="parameters-footer tw-mt-4 tw-flex tw-flex-none tw-flex-col-reverse tw-items-end tw-justify-end tw-gap-2 tw-border-t tw-border-gtr-border/60 tw-pt-4 sm:tw-flex-row">
                    <Button type="button" className="login-button-close" onClick={close} disabled={saving || confirmingClose} startIcon={<CloseOutlined />} sx={{ width: { xs: "100%", sm: 132 } }}>Fechar</Button>
                    <Button type="button" className="login-button" onClick={save} disabled={!features || loading || saving || confirmingClose} startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveOutlined />} sx={{ width: { xs: "100%", sm: 132 } }}>{saving ? "Salvando..." : "Salvar"}</Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default CustomerFeaturesModal;
